import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BriefcaseBusiness,
  CalendarDays,
  Clock3,
  FileText,
  Search,
  Upload,
  UserRound,
} from "lucide-react";

import api from "../../api/axios";
import AdminNavbar from "../../components/layout/AdminNavbar";

const statusTabs = ["All", "Pending", "Accepted", "Rejected", "In Clearance", "Completed"];

const statusStyle = {
  Pending: "bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900",
  Accepted: "bg-[var(--accent-light)] text-[var(--heading-secondary)] border-[var(--border-color)] dark:bg-[var(--accent-subtle)] dark:text-blue-200 dark:border-[var(--border-color)]",
  Rejected: "bg-red-50 text-red-700 border-red-100 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900",
  "In Clearance": "bg-[var(--accent-light)] text-[var(--heading-secondary)] border-violet-100 dark:bg-[var(--accent-subtle)] dark:text-[var(--heading-secondary)] dark:border-violet-900",
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-200 dark:border-emerald-900",
};

const checklistLabels = {
  laptopReturned: "Asset return: laptop/system",
  idCardReturned: "Asset return: ID card",
  pendingSalaryCleared: "Finance: pending salary cleared",
  expensesSettled: "Finance: expenses settled",
  companyAccessRemoved: "Access: company accounts removed",
  teamLeadApproved: "Manager: handover approved",
  hrApproved: "HR: exit interview/final approval",
  finalSettlementCompleted: "Final settlement completed",
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const StatusPill = ({ status }) => (
  <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-black ${statusStyle[status] || statusStyle.Pending}`}>
    {status || "Pending"}
  </span>
);

const DocumentLink = ({ document, label }) => {
  if (!document?.data) return null;
  return (
    <a
      href={document.data}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-xs font-black text-[var(--text-primary)]"
    >
      <FileText size={14} />
      {label || document.name || "Open document"}
    </a>
  );
};

const AdminOffboarding = () => {
  const [records, setRecords] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [status, setStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [indicator, setIndicator] = useState("");
  const [draft, setDraft] = useState({
    status: "Pending",
    approvedLastWorkingDate: "",
    adminNote: "",
    finalSettlementNote: "",
    clearance: {},
  });
  const [experienceLetter, setExperienceLetter] = useState(null);
  const [relievingLetter, setRelievingLetter] = useState(null);

  const selected = useMemo(
    () => records.find((record) => record._id === selectedId) || records[0] || null,
    [records, selectedId],
  );

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/admin/offboarding", {
        params: {
          status,
          query: search.trim() || undefined,
          limit: 160,
        },
        cache: false,
      });
      const next = Array.isArray(res.data) ? res.data : [];
      setRecords(next);
      if (!selectedId && next[0]?._id) setSelectedId(next[0]._id);
    } catch (error) {
      console.error("Failed to load offboarding:", error);
    } finally {
      setLoading(false);
    }
  }, [search, selectedId, status]);

  useEffect(() => {
        document.title = "Offboarding | Savrion Technologies";
    const timeoutId = window.setTimeout(fetchRecords, search.trim() ? 250 : 0);
    return () => window.clearTimeout(timeoutId);
  }, [fetchRecords, search]);

  useEffect(() => {
        document.title = "Offboarding | Savrion Technologies";
    if (!selected) return;
    setDraft({
      status: selected.status || "Pending",
      approvedLastWorkingDate: toDateInput(selected.approvedLastWorkingDate || selected.requestedLastWorkingDate),
      adminNote: selected.adminNote || "",
      finalSettlementNote: selected.finalSettlementNote || "",
      clearance: selected.clearance || {},
    });
    setExperienceLetter(null);
    setRelievingLetter(null);
  }, [selected]);

  const counts = useMemo(() => {
    return records.reduce((acc, record) => {
      acc.All += 1;
      acc[record.status] = (acc[record.status] || 0) + 1;
      return acc;
    }, { All: 0, Pending: 0, Accepted: 0, Rejected: 0, "In Clearance": 0, Completed: 0 });
  }, [records]);

  const saveSelected = async (nextStatus = draft.status) => {
    if (!selected) return;

    setSaving(true);
    setIndicator("Saving offboarding...");
    const previous = records;
    const optimistic = {
      ...selected,
      ...draft,
      status: nextStatus,
      approvedLastWorkingDate: draft.approvedLastWorkingDate,
    };
    setRecords((current) => current.map((record) => record._id === selected._id ? optimistic : record));

    try {
      const [experienceData, relievingData] = await Promise.all([
        experienceLetter ? fileToDataUrl(experienceLetter) : "",
        relievingLetter ? fileToDataUrl(relievingLetter) : "",
      ]);

      const res = await api.patch(`/api/admin/offboarding/${selected._id}`, {
        status: nextStatus,
        approvedLastWorkingDate: draft.approvedLastWorkingDate,
        adminNote: draft.adminNote,
        finalSettlementNote: draft.finalSettlementNote,
        clearance: draft.clearance,
        experienceLetterName: experienceLetter?.name || "",
        experienceLetter: experienceData,
        relievingLetterName: relievingLetter?.name || "",
        relievingLetter: relievingData,
      }, { skipSaveToast: true });

      setRecords((current) => current.map((record) => record._id === selected._id ? res.data : record));
      setIndicator("Offboarding updated.");
      setExperienceLetter(null);
      setRelievingLetter(null);
    } catch (error) {
      setRecords(previous);
      setIndicator(error.response?.data?.message || "Could not update offboarding.");
    } finally {
      setSaving(false);
      window.setTimeout(() => setIndicator(""), 2400);
    }
  };

  const completedChecklist = Object.keys(checklistLabels).filter((key) => draft.clearance?.[key]).length;
  const checklistTotal = Object.keys(checklistLabels).length;
  const clearanceComplete = completedChecklist === checklistTotal;

  return (
    <div className="flex min-h-screen flex-col bg-[var(--bg-primary)] text-[var(--text-primary)] xl:flex-row">
      <AdminNavbar />

      <main className="min-w-0 flex-1 px-4 py-6 md:px-6">
        <div className="space-y-5">
          <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-5">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-wide text-[var(--accent-primary)] dark:text-[var(--heading-secondary)]">
                  <BriefcaseBusiness size={16} />
                  Exit management
                </div>
                <h1 className="text-2xl font-black md:text-3xl">Offboarding flow</h1>
                <p className="mt-1 text-sm text-[var(--text-secondary)]">
                  Approve resignations, manage clearance, final settlement and final documents.
                </p>
              </div>

              {indicator && (
                <div className="rounded-xl border border-[var(--border-color)] bg-[var(--accent-light)] px-4 py-3 text-sm font-black text-[var(--heading-secondary)] dark:border-[var(--border-color)] dark:bg-[var(--accent-subtle)] dark:text-blue-200">
                  {indicator}
                </div>
              )}
            </div>
          </section>

          <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-2">
                {statusTabs.map((item) => (
                  <button
                    type="button"
                    key={item}
                    onClick={() => setStatus(item)}
                    className={`rounded-xl border px-4 py-2 text-sm font-black transition ${
                      status === item
                        ? "border-blue-500 bg-[var(--accent-primary)] text-white"
                        : "border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                    }`}
                  >
                    {item} {counts[item] ? `(${counts[item]})` : ""}
                  </button>
                ))}
              </div>

              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 lg:w-[360px]">
                <Search size={17} className="text-[var(--text-secondary)]" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search employee, ID, team"
                  className="min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none"
                />
              </div>
            </div>
          </section>

          <div className="grid gap-5 xl:grid-cols-[360px_1fr]">
            <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)]">
              <div className="flex items-center justify-between border-b border-[var(--border-color)] px-4 py-3">
                <p className="text-sm font-black">{loading ? "Loading..." : `${records.length} requests`}</p>
                <Clock3 size={16} className="text-[var(--text-secondary)]" />
              </div>

              {!records.length && !loading ? (
                <div className="p-8 text-center text-sm font-bold text-[var(--text-secondary)]">
                  No offboarding requests found.
                </div>
              ) : (
                <div className="max-h-[650px] overflow-auto p-2">
                  {records.map((record) => (
                    <button
                      type="button"
                      key={record._id}
                      onClick={() => setSelectedId(record._id)}
                      className={`mb-2 w-full rounded-xl border p-3 text-left transition ${
                        selected?._id === record._id
                          ? "border-blue-400 bg-[var(--accent-light)] dark:bg-[var(--accent-subtle)]"
                          : "border-[var(--border-color)] bg-[var(--bg-primary)]"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-black">{record.employee?.name || "Employee"}</p>
                          <p className="truncate text-xs font-semibold text-[var(--text-secondary)]">
                            {record.employee?.employeeId || "-"} - {record.employee?.team || "-"}
                          </p>
                        </div>
                        <StatusPill status={record.status} />
                      </div>
                      <p className="mt-2 text-xs font-bold text-[var(--text-secondary)]">
                        Last working: {formatDate(record.approvedLastWorkingDate || record.requestedLastWorkingDate)}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)]">
              {!selected ? (
                <div className="p-10 text-center text-sm font-bold text-[var(--text-secondary)]">
                  Select an offboarding request.
                </div>
              ) : (
                <div>
                  <div className="border-b border-[var(--border-color)] p-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase text-[var(--text-secondary)]">
                          <UserRound size={15} />
                          Employee
                        </div>
                        <h2 className="text-2xl font-black">{selected.employee?.name || "Employee"}</h2>
                        <p className="mt-1 text-sm font-semibold text-[var(--text-secondary)]">
                          {selected.employee?.employeeId || "-"} - {selected.employee?.designation || selected.employee?.team || "-"}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <DocumentLink document={selected.resignationLetter} label="Resignation letter" />
                        <DocumentLink document={selected.experienceLetter} label="Experience letter" />
                        <DocumentLink document={selected.relievingLetter} label="Relieving letter" />
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-5 p-5 2xl:grid-cols-[1fr_360px]">
                    <div className="space-y-5">
                      <div className="grid gap-4 lg:grid-cols-2">
                        <label className="block">
                          <span className="mb-2 block text-xs font-black uppercase text-[var(--text-secondary)]">Status</span>
                          <select
                            value={draft.status}
                            onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value }))}
                            className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-bold outline-none"
                          >
                            {statusTabs.filter((item) => item !== "All").map((item) => (
                              <option key={item} value={item}>{item}</option>
                            ))}
                          </select>
                        </label>

                        <label className="block">
                          <span className="mb-2 block text-xs font-black uppercase text-[var(--text-secondary)]">Approved last working date</span>
                          <div className="flex items-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2">
                            <CalendarDays size={17} className="text-[var(--text-secondary)]" />
                            <input
                              type="date"
                              value={draft.approvedLastWorkingDate}
                              onChange={(event) => setDraft((current) => ({ ...current, approvedLastWorkingDate: event.target.value }))}
                              className="min-w-0 flex-1 bg-transparent text-sm font-bold outline-none"
                            />
                          </div>
                        </label>
                      </div>

                      <label className="block">
                        <span className="mb-2 block text-xs font-black uppercase text-[var(--text-secondary)]">Admin note</span>
                        <textarea
                          value={draft.adminNote}
                          onChange={(event) => setDraft((current) => ({ ...current, adminNote: event.target.value }))}
                          rows={3}
                          className="w-full resize-none rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-3 text-sm font-semibold outline-none"
                          placeholder="Reason for approval/rejection or clearance note"
                        />
                      </label>

                      <label className="block">
                        <span className="mb-2 block text-xs font-black uppercase text-[var(--text-secondary)]">Final settlement note</span>
                        <textarea
                          value={draft.finalSettlementNote}
                          onChange={(event) => setDraft((current) => ({ ...current, finalSettlementNote: event.target.value }))}
                          rows={3}
                          className="w-full resize-none rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-3 text-sm font-semibold outline-none"
                          placeholder="Pending salary, expense settlement, FNF remarks"
                        />
                      </label>

                      <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-4">
                        <div className="mb-3 flex items-center justify-between">
                          <p className="text-sm font-black">Clearance checklist</p>
                          <span className="text-xs font-black text-[var(--text-secondary)]">{completedChecklist}/{Object.keys(checklistLabels).length}</span>
                        </div>
                        <div className="grid gap-2 md:grid-cols-2">
                          {Object.entries(checklistLabels).map(([key, label]) => (
                            <label key={key} className="flex cursor-pointer items-center gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] px-3 py-3 text-sm font-bold">
                              <input
                                type="checkbox"
                                checked={Boolean(draft.clearance?.[key])}
                                onChange={(event) => setDraft((current) => ({
                                  ...current,
                                  clearance: { ...current.clearance, [key]: event.target.checked },
                                }))}
                                className="h-4 w-4 accent-blue-600"
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="grid gap-3 md:grid-cols-2">
                        <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-dashed border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-3 text-sm font-bold">
                          <span className="flex min-w-0 items-center gap-2">
                            <Upload size={16} className="text-[var(--accent-primary)]" />
                            <span className="truncate">{experienceLetter?.name || "Upload experience letter"}</span>
                          </span>
                          <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(event) => setExperienceLetter(event.target.files?.[0] || null)} />
                        </label>
                        <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-dashed border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-3 text-sm font-bold">
                          <span className="flex min-w-0 items-center gap-2">
                            <Upload size={16} className="text-[var(--accent-primary)]" />
                            <span className="truncate">{relievingLetter?.name || "Upload relieving letter"}</span>
                          </span>
                          <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(event) => setRelievingLetter(event.target.files?.[0] || null)} />
                        </label>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {["Accepted", "Rejected", "In Clearance", "Completed"].map((item) => (
                          <button
                            type="button"
                            key={item}
                            disabled={saving || (item === "Completed" && !clearanceComplete)}
                            onClick={() => saveSelected(item)}
                            title={item === "Completed" && !clearanceComplete ? "Complete every clearance item first" : `Mark ${item}`}
                            className="rounded-xl bg-[var(--accent-primary)] px-4 py-2 text-sm font-black text-white disabled:opacity-60"
                          >
                            Mark {item}
                          </button>
                        ))}
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => saveSelected()}
                          className="rounded-xl border border-[var(--border-color)] px-4 py-2 text-sm font-black disabled:opacity-60"
                        >
                          {saving ? "Saving..." : "Save changes"}
                        </button>
                      </div>
                      {!clearanceComplete && (
                        <p className="text-xs font-bold text-amber-600">
                          Complete all {checklistTotal} clearance items before marking this employee completed.
                        </p>
                      )}
                    </div>

                    <aside className="space-y-4">
                      <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-4">
                        <p className="mb-3 text-sm font-black">Request details</p>
                        <dl className="space-y-3 text-sm">
                          <div>
                            <dt className="text-xs font-black uppercase text-[var(--text-secondary)]">Requested on</dt>
                            <dd className="font-bold">{formatDateTime(selected.createdAt)}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-black uppercase text-[var(--text-secondary)]">Requested last date</dt>
                            <dd className="font-bold">{formatDate(selected.requestedLastWorkingDate)}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-black uppercase text-[var(--text-secondary)]">Notice days</dt>
                            <dd className="font-bold">{selected.noticeDays || 0} days</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-black uppercase text-[var(--text-secondary)]">Reason</dt>
                            <dd className="font-semibold text-[var(--text-secondary)]">{selected.reason}</dd>
                          </div>
                        </dl>
                      </div>

                      <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-4">
                        <p className="mb-3 text-sm font-black">Timeline</p>
                        <div className="space-y-3">
                          {(selected.timeline || []).map((item, index) => (
                            <div key={`${item.label}-${index}`} className="flex gap-3">
                              <span className="mt-1 h-2 w-2 rounded-full bg-[var(--accent-primary)]" />
                              <div>
                                <p className="text-sm font-black">{item.label}</p>
                                <p className="text-xs text-[var(--text-secondary)]">{formatDateTime(item.at)} {item.byName ? `- ${item.byName}` : ""}</p>
                                {item.note && <p className="mt-1 text-xs text-[var(--text-secondary)]">{item.note}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </aside>
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminOffboarding;


